import React, { useState, useEffect } from 'react'
import styled, { css } from 'styled-components'
import { Form, Field } from 'react-final-form'
import { navigate, Link } from 'gatsby'
import { Router } from '@reach/router'
import { Helmet } from 'react-helmet'
import PropTypes from 'prop-types'
import FondsImg from '../images/fonds-img.svg'
import polishFlag from '../images/polishFlag.svg'
import unionFlag from '../images/unionFlag.svg'
import IBE from '../images/IBE.svg'
import USWPS from '../images/USWPS.svg'
import logo from '../images/logo.svg'
import {
  lowerLetter,
  number,
  upperLetter,
  required,
  validateEquality,
  validateLength,
  mapValidators,
  specialChar,
} from '../utils/validation'
import App from './App'
import colors from '../styles/colors'
import TextInput from '../components/TextInput/index'
import NewPasswordTextInput from '../components/NewPasswordTextInput'
import { flexBoxStyles } from '../components/Flexbox'
import Button from '../components/Button'
import arrow from '../images/arrowRight.svg'
import { isLoggedIn } from '../utils/localStorage'
import api from '../utils/api'
import RequirementList from '../components/NewPasswordTextInput/RequirementList'
import { mainContentTagId } from '../utils/constants'
import SkipNav from '../components/SkipNav/SkipNav'

const fieldWidth = 350

const SubmitMessage = () => (
  <>
    <H2 margin>Sprawdź pocztę</H2>
    <div tabIndex="0" role="alert">
      <p>Na twój adres email został wysłany link do ustawienia nowego hasła.</p>
      <p>
        Sprawdź również swoją skrzynkę ze spamem, a jeśli nie otrzymałeś od nas
        maila to skontaktuj się z administratorem,
      </p>
    </div>
    <StyledLink $fieldWidth={fieldWidth} to="/app/login">
      <span>Zaloguj się</span>

      <Arrow alt="" src={arrow} aria-hidden="true" />
    </StyledLink>
  </>
)

export const LoginForm = ({ onSubmit }) => (
  <Form onSubmit={onSubmit} autocomplete="new-password">
    {({
      handleSubmit,
      hasValidationErrors,
      submitErrors,
      touched,
      errors,
      ...props
    }) => (
      <FormWrapper
        data-testid="login-form"
        fieldWidth={fieldWidth}
        justify="center"
        align="center"
      >
        <H1 margin>Kompleksowa Analiza Procesów Poznawczych</H1>

        {renderFormWarningIfNecessary(touched, errors)}
        <FormHeader marginBottom="50px">Wszystkie pola są wymagane</FormHeader>
        <Field
          autocomplete="off"
          component={TextInput}
          width={fieldWidth}
          label="Użytkownik"
          type="text"
          name="login"
          validate={required}
          isRequired={true}
        />
        <Field
          autocomplete="off"
          component={TextInput}
          width={fieldWidth}
          label="Hasło"
          isPassword
          type="text"
          name="password"
          validate={required}
          errorMessages={{
            generic: 'Podany login lub hasło jest niepoprawne',
          }}
          isRequired={true}
          roleForErrorMessage="alert"
        />
        <StyledLink
          to="/app/login/forgot-password"
          $fieldWidth={fieldWidth}
          $margin="-10px 0 0 0"
        >
          <span>Zapomniałem(-am) hasła</span>
          <Arrow alt="" src={arrow} aria-hidden="true" />
        </StyledLink>
        <StyledButton
          width={`${fieldWidth}px`}
          label="Zaloguj się"
          onClick={handleSubmit}
          disabled={hasValidationErrors}
        />
      </FormWrapper>
    )}
  </Form>
)

export const ForgotPasswordForm = ({ onSubmit }) => (
  <Form onSubmit={onSubmit} autoComplete="false">
    {({ handleSubmit, invalid, submitSucceeded, touched, errors }) => (
      <FormWrapper
        data-testid="forgot-password"
        fieldWidth={fieldWidth}
        justify="center"
        align="center"
      >
        {submitSucceeded ? (
          <SubmitMessage />
        ) : (
          <>
            <H2>Zresetuj hasło</H2>
            <p>
              Podaj swój login. Na twój adres email zostanie wysłany link do
              zmiany hasła.
            </p>

            {renderFormWarningIfNecessary(touched, errors)}
            <FormHeader marginBottom="50px">
              Wszystkie pola są wymagane
            </FormHeader>
            <Field
              component={TextInput}
              width={fieldWidth}
              label="Login"
              type="text"
              name="login"
              validate={required}
              autocomplete="username"
              isRequired={true}
            />

            <StyledLink
              $fieldWidth={fieldWidth}
              to="/app/login"
              $margin="-10px 0 0 0"
            >
              <span>Zaloguj się</span>
              <Arrow alt="" src={arrow} aria-hidden="true" />
            </StyledLink>
            <StyledButton
              data-testid="submit-forgot-password"
              width={`${fieldWidth}px`}
              label="Zresetuj hasło"
              onClick={handleSubmit}
              disabled={invalid}
            />
          </>
        )}
      </FormWrapper>
    )}
  </Form>
)

export const SetPasswordForm = ({ onSubmit, ...props }) => {
  const [tokenStatus, setStatus] = useState(null)

  useEffect(() => {
    const fetchData = async () => {
      try {
        await api('/user/rest/reset-password/verify', {
          method: 'POST',
          headers: { responseType: 'json' },
          payload: { id: parseInt(props.id, 10), token: props.token },
        })

        setStatus(200)
      } catch (e) {
        setStatus(e.response.status)
      }
    }

    fetchData()
  }, [props.id, props.token])

  switch (tokenStatus) {
    case 200:
      return (
        <Form
          autoComplete="false"
          onSubmit={(values) =>
            onSubmit({ ...values, id: props.id, token: props.token })
          }
          validate={validateEquality('password')}
        >
          {({ handleSubmit, invalid, values, touched, errors, ...rest }) => (
            <FormWrapper
              data-testid="set-password-form"
              fieldWidth={fieldWidth}
              justify="center"
              align="center"
            >
              <H2> Ustaw nowe hasło</H2>

              {renderFormWarningIfNecessary(touched, errors)}
              <FormHeader marginBottom="50px">
                Wszystkie pola są wymagane
              </FormHeader>
              <Field
                component={NewPasswordTextInput}
                width={fieldWidth}
                label="Hasło"
                type="text"
                isPassword
                name="password"
                validate={mapValidators(
                  lowerLetter,
                  number,
                  required,
                  specialChar,
                  validateLength(6),
                  upperLetter
                )}
                autocomplete="new-password"
                isRequired={true}
              />
              <Field
                data-testid="repeat-password"
                component={TextInput}
                width={fieldWidth}
                label="Powtórz hasło"
                type="text"
                isPassword
                name="repeatPassword"
                validate={mapValidators(
                  lowerLetter,
                  number,
                  required,
                  specialChar,
                  validateLength(6),
                  upperLetter
                )}
                autocomplete="new-password"
                isRequired={true}
              />
              <RequirementList
                error={errors.password}
                passwordEmpty={!values.password}
                password={values.repeatPassword}
                value={values.password}
                requirements={[
                  'tooShort',
                  'noUppercase',
                  'noLowercase',
                  'noNumber',
                  'noSpecialChar',
                ]}
              />

              <StyledButton
                data-testid="set-password-button"
                width={`${fieldWidth}px`}
                label="Zmień hasło"
                onClick={handleSubmit}
                disabled={invalid || values.password !== values.repeatPassword}
              />
            </FormWrapper>
          )}
        </Form>
      )

    case 404:
      return (
        <FormWrapper justify="center" align="center">
          <H1>404</H1>
          <H2>Niepoprawny link</H2>
        </FormWrapper>
      )
    case 409:
      return (
        <FormWrapper justify="center" align="center">
          <H2>
            Link został już wykorzystany.{' '}
            <Link to="/app/login/forgot-password">Kliknij tutaj</Link>,
            <br />
            aby wygenerować nowy link.
          </H2>
        </FormWrapper>
      )
    case 410:
      return (
        <FormWrapper justify="center" align="center">
          <H2>
            Link został zdeaktywowany przez zbyt długi czas oczekiwania na jego
            użycie. <Link to="/app/login/forgot-password">Kliknij tutaj</Link>,
            <br />
            aby wygenerować nowy link.
          </H2>
        </FormWrapper>
      )
    default:
      return <FormWrapper justify="center" align="center" />
  }
}

const Login = (props) => {
  return (
    <App.Consumer>
      {({ login, resetPassword, setPassword }) => {
        if (isLoggedIn()) {
          navigate('/app')
          return null
        }
        return (
          <Wrapper>
            <Helmet htmlAttributes={{ lang: 'pl-PL' }}>
              <title>
                Ekran logowania - Kompleksowa Analiza Procesów Poznawczych
              </title>
            </Helmet>

            <SkipNav idTagToSkip={mainContentTagId} />

            <Logo alt="Kompleksowa Analiza Procesów Poznawczych" src={logo} />
            <ImgWrapperTop>
              <Img alt="Fundusze Europejskie" src={FondsImg} $width="10%" />
              <Img alt="Pzeczpospolita Polska" src={polishFlag} $width="18%" />
              <Img alt="Unia Europejska" src={unionFlag} $width="19%" />
            </ImgWrapperTop>

            <main id={mainContentTagId}>
              <Router>
                <LoginForm onSubmit={login} {...props} path="/" />
                <ForgotPasswordForm
                  onSubmit={resetPassword}
                  path="forgot-password"
                />
                <SetPasswordForm
                  path="set-password/:id/:token"
                  onSubmit={setPassword}
                />
              </Router>
            </main>

            <ImgWrapperBottom>
              <Img alt="Instytut badań edukacyjnych" src={IBE} $width="20%" />
              <Img alt="Uniwersytet SWPS" src={USWPS} $width="17%" />
            </ImgWrapperBottom>
          </Wrapper>
        )
      }}
    </App.Consumer>
  )
}

const renderFormWarningIfNecessary = (touched, errors) => {
  const shouldShowWarning = Object.keys(touched).some(
    (fieldName) => touched[fieldName] === true && errors[fieldName] != null
  )

  return shouldShowWarning === false ? (
    <FormHeader minHeight="30px" />
  ) : (
    <FormHeader role="alert">Formularz zawiera błędy</FormHeader>
  )
}

Login.contextTpes = {
  login: PropTypes.func.required,
  token: PropTypes.string,
}

export default Login

const Logo = styled.img`
  width: 180px;
  height: 40px;
  margin: 0.6rem;
`
const ImgWrapperTop = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1rem;
`
const ImgWrapperBottom = styled.div`
  display: flex;
  justify-content: center;
`

const Img = styled.img`
  ${({ $width }) => $width && ` width: ${$width}`};
  margin: 0 4rem;
`

const StyledLink = styled(Link)`
  ${({ $margin }) => $margin && `margin: ${$margin}`};
  display: flex;
  width: 100%;
  text-decoration: none;
  font-size: 0.75rem;
  color: ${colors.monza};
  max-width: ${({ $fieldWidth }) => $fieldWidth}px;
  > span {
    margin-right: 10px;
  }
`
const Arrow = styled.img`
  ${StyledLink}:hover & {
    transform: translateX(5px);
    transition: all 0.2s ease-in;
  }
`

const H2 = styled.h2`
  ${(props) => props.margin && `margin-bottom: 100px;`}
  font-weight: 400;
`
const H1 = styled.h1`
  ${(props) => props.margin && `margin-bottom: 100px;`}
  font-weight: 400;
`

const Wrapper = styled.div`
  display: grid;
  grid: 1fr /1fr;
`

const FormWrapper = styled.form`
  width: ${(props) => props.fieldWidth && props.fieldWidth}px;
  max-width: 90%;
  margin: 5.5rem auto;
  display: flex;
  flex-direction: column;
  justify-content: center;

  ${flexBoxStyles};
`

const FormHeader = styled.div`
  width: 100%;
  ${({ marginBottom }) =>
    marginBottom != null &&
    css`
      margin-bottom: ${marginBottom};
    `};
  visibility: ${({ isHidden }) => (isHidden === true ? 'hidden' : 'visible')};
  ${({ minHeight }) =>
    minHeight != null &&
    css`
      min-height: ${minHeight};
    `};
`

const StyledButton = styled(Button)`
  opacity: 1;
  ${({ disabled }) =>
    disabled === true &&
    css`
      color: black;
      background-color: ${colors.readOnly};
      border-color: ${colors.readOnly};
      cursor: auto;
      &:hover {
        background-color: ${colors.readOnly};
      }
    `}; // kolor dobrany tak, aby spelnial wymog kontrastu wcag na poziomie AA
`
